/**
 * Read model for the toolbar's auto-refresh control (UX review: it read a flat
 * "Auto-refresh" whether every tile was on a 30s cadence or none of them were,
 * with no hint of when the next cycle lands).
 *
 * Deliberately a PURE read model, not a schedule: `services/refreshScheduler.ts`
 * remains the single timer authority. The cadence comes from the tiles' own
 * `refresh` config (what the scheduler was registered with) and the cycle
 * position from the runtime store's last-run timestamps (what the scheduler
 * actually did) — no timer is started here and nothing is inferred that the
 * scheduler hasn't already done. Two documented approximations: a run's own
 * duration sits between "tick fired" and "result settled", so the countdown
 * trails the scheduler's internal tick by that much (milliseconds on a healthy
 * tile); and a MANUAL refresh re-anchors this display without moving the
 * scheduler's timer. Both are cosmetic — an exact read would need the
 * scheduler instance itself, which only `DashboardTab` holds.
 */
import { isQueryBackedTile, type DashboardTile } from '../types/dashboardTypes';

export type AutoRefreshStatus = {
  /** Query tiles on an interval cadence — 0 means auto-refresh is off. */
  tileCount: number;
  /** The one shared cadence in seconds; null when tiles disagree ("mixed"). */
  seconds: number | null;
  /** Control label: 'Auto-refresh' / 'Auto-refresh · 30s' / '… · mixed'. */
  label: string;
  /** Whole seconds to the next cycle; null until some tile has actually run. */
  remainingSeconds: number | null;
  /** 0..1 through the current cycle; null until some tile has actually run. */
  progress: number | null;
  /** Start of the cycle `progress` measures — a stable key for the bar. */
  cycleStartedAt: number | null;
};

/** Compact cadence text: 45s, 1m, 5m, 1h. */
export const formatCadence = (seconds: number): string => {
  if (seconds >= 3600 && seconds % 3600 === 0) return `${seconds / 3600}h`;
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60}m`;
  return `${seconds}s`;
};

const OFF: AutoRefreshStatus = {
  tileCount: 0,
  seconds: null,
  label: 'Auto-refresh',
  remainingSeconds: null,
  progress: null,
  cycleStartedAt: null,
};

/**
 * @param tiles the dashboard's tiles (note tiles and manual tiles are ignored)
 * @param lastRunAtByTileId epoch ms of each tile's last settled run, if any
 * @param now epoch ms — an input so the whole thing stays pure/testable
 */
export function autoRefreshStatus(
  tiles: readonly DashboardTile[],
  lastRunAtByTileId: Readonly<Record<string, number | undefined>>,
  now: number,
): AutoRefreshStatus {
  const cadences: Array<{ id: string; seconds: number }> = [];
  for (const tile of tiles) {
    if (!isQueryBackedTile(tile)) continue;
    if (tile.refresh.mode !== 'interval' || tile.refresh.seconds <= 0) continue;
    cadences.push({ id: tile.id, seconds: tile.refresh.seconds });
  }
  if (cadences.length === 0) return OFF;

  const distinct = new Set(cadences.map((c) => c.seconds));
  const seconds = distinct.size === 1 ? [...distinct][0] : null;
  const label = `Auto-refresh · ${seconds === null ? 'mixed' : formatCadence(seconds)}`;

  // The dashboard's next cycle is the soonest tile due — that's the moment
  // something on screen visibly changes.
  let next: { dueAt: number; cycleMs: number } | null = null;
  for (const cadence of cadences) {
    const lastRunAt = lastRunAtByTileId[cadence.id];
    if (lastRunAt === undefined) continue;
    const cycleMs = cadence.seconds * 1000;
    const dueAt = lastRunAt + cycleMs;
    if (next === null || dueAt < next.dueAt) next = { dueAt, cycleMs };
  }
  if (next === null) {
    return { tileCount: cadences.length, seconds, label, remainingSeconds: null, progress: null, cycleStartedAt: null };
  }

  const remainingMs = Math.max(0, next.dueAt - now);
  return {
    tileCount: cadences.length,
    seconds,
    label,
    remainingSeconds: Math.ceil(remainingMs / 1000),
    progress: Math.min(1, Math.max(0, 1 - remainingMs / next.cycleMs)),
    cycleStartedAt: next.dueAt - next.cycleMs,
  };
}
